<template>
  <div>
    <section-page title="Contacts" full-height>
      <template v-slot:right>
        <table-buttons
          ref="contactsTableButtons"
          show-refresh
          show-presets
          :show-actions="showAction"
          :show-download-button="hasExportIntegration"
          show-columns-button
          show-filters-button
          :filter-count="filterCount"
          :presets="contactPresets"
          :default-preset="defaultPreset"
          :actions="actions"
          :do-refresh="refreshTable"
          download-label="contacts"
          :do-download="downloadCSV"
          :is-downloading="downloading"
          :do-columns-apply="applyColumnsChange"
          :do-columns-reset="resetColumns"
          :do-filters-apply="refreshTable"
          :do-filters-reset="resetFilters"
          :do-manage-presets="managePreset"
          @presetChanged="changePreset"
          @runAction="runAction"
        >
          <template v-slot:columns>
            <div class="mb-50" v-for="(column, index) in filterTableColumns">
              <b-switch
                v-if="column.field !== 'full_name'"
                :value="true"
                v-model="column.visible"
                size="is-small"
                :key="index"
                expanded
                >{{ column.label }}</b-switch
              >
            </div>
          </template>

          <template v-slot:filters>
            <contacts-filter store-module="contacts" filter-name="contacts" />
          </template>
        </table-buttons>

        <button-default
          class="ml-50"
          icon-left="plus"
          @click="$refs.contactModal.open()"
          is-short
          >New Contact</button-default
        >
      </template>

      <table-paginated
        ref="contactsTable"
        is-checkable
        sticky-checkbox
        show-per-page-option
        page-input
        :load-data="loadContacts"
        :checked-rows-data="checkedActionRowsData"
        :columns="contactsTableColumn"
        :per-page="25"
        :actions-width="100"
        :default-sort="['full_name', 'asc']"
        :has-mobile-cards="false"
        empty-heading="You have no contacts."
        entries-label="contacts"
        @rowsChecked="manageRowsChecked"
      />
    </section-page>

    <section-page title="Mass Action Logs">
      <template v-slot:right>
        <table-buttons show-refresh :do-refresh="refreshActionTable" />
      </template>

      <table-paginated
        ref="actionsTable"
        show-per-page-option
        :load-data="loadProcessedMassActions"
        :columns="actionTableColumns"
        :per-page="10"
        empty-heading="You have no action history."
        entries-label="items"
      />
    </section-page>

    <modal-container>
      <modal-default ref="managePresetsModal" title="Manage Presets" is-wide>
        <user-default-preset-form />

        <b-tabs
          v-model="currentPresetTab"
          class="tabs_vertical_modal"
          vertical
          expanded
          :animated="false"
          @input="handlePresetTabChange"
        >
          <b-tab-item value="0" label="New Preset">
            <preset-form
              v-if="currentPresetTab === 0"
              store-module="contacts"
              :preset-id="activePresetId"
            />
          </b-tab-item>

          <b-tab-item
            v-for="(preset, index) in contactPresets"
            :key="preset.id"
            :value="String(index) + 1"
            :label="preset.label"
          >
            <preset-form
              v-if="currentPresetTab === index + 1"
              store-module="contacts"
              :preset-id="activePresetId"
              @deleted="handlePresetTabChange(0)"
            />
          </b-tab-item>
        </b-tabs>
      </modal-default>
    </modal-container>

    <modal-container>
      <modal-default ref="typesModal" title="Manage Contact Types">
        <p class="modal-description">
          Selected {{ checkedActionRowsData.length }} out of
          {{ totalContacts }} contacts
        </p>

        <form-field-select-contact-types
          v-model="actionForm.types"
          :validation-errors="validationErrors.types"
        ></form-field-select-contact-types>

        <template v-slot:footer>
          <button-default
            @click="saveTypes"
            :loading="saving"
            :disabled="saving"
          >
            Save
          </button-default>
        </template>
      </modal-default>
    </modal-container>

    <modal-container>
      <modal-default ref="tagsModal" title="Manage Contact Tags">
        <p class="modal-description">
          Selected {{ checkedActionRowsData.length }} out of
          {{ totalContacts }} contacts
        </p>

        <form-field-collection
          v-model="actionForm.tags"
          :options="contactTags"
          field="label"
          placeholder="Select tag"
          icon="label"
          :validation-errors="validationErrors.tags"
          :loading="saving"
          :disabled="saving"
        ></form-field-collection>
        <p class="note mb-400">
          Note: Tags must be created via
          <router-link :to="{ name: 'settings', params: { tab: 'contacts' } }"
            >Contact Settings</router-link
          >
          before attaching to a contact
        </p>

        <template v-slot:footer>
          <button-default
            @click="saveTags"
            :loading="saving"
            :disabled="saving"
          >
            Save
          </button-default>
        </template>
      </modal-default>
    </modal-container>

    <modal-container>
      <modal-crud-form
        ref="contactModal"
        title="Contact"
        form-component="contact-form"
        include-create-and-view-contact
      ></modal-crud-form>
    </modal-container>
  </div>
</template>

<script>
import ContactForm from "../../forms/ContactForm";
import TablePaginated from "../../components/table/TablePaginated";
import utils from "../../services/utils";
import router from "../../router";
import AppInfoTip from "../../components/app/AppInfoTip";
import LeadsFilters from "../leads/components/LeadsFilters.vue";
import TableButtons from "../../components/table/TableButtons.vue";
import ContactsFilter from "./components/ContactsFilters.vue";
import api from "../../api";
import PresetForm from "../../forms/PresetForm.vue";
import { ENUMS } from "../../services/enums";
import dates from "../../services/dates";
import UserDefaultPresetForm from "../../forms/UserDefaultPresetForm.vue";
import { SnackbarProgrammatic as Snackbar } from "buefy";

export default {
  name: "ContactsPageDesktop",
  components: {
    UserDefaultPresetForm,
    PresetForm,
    ContactsFilter,
    TableButtons,
    LeadsFilters,
    TablePaginated,
    ContactForm,
    AppInfoTip,
  },
  metaInfo: {
    title: "Contacts",
  },
  data() {
    return {
      saving: false,
      downloading: false,
      showAction: false,
      totalContacts: 0,
      filterCount: 0,
      currentPresetTab: 0,
      activePresetId: 0,
      actionTableColumns: [
        {
          label: "Name",
          field: "contact_name",
        },
        {
          label: "Action",
          field: "type",
          display(row) {
            const statusEnum = Object.values(
              ENUMS.ContactMassActionTypeEnum
            ).find((type) => type.value === row.type);
            return statusEnum ? `Manage ${statusEnum.label}` : "--";
          },
        },
        {
          label: "Status",
          field: "queueable_status",
          display(row) {
            const statusEnum = Object.values(ENUMS.QueueableStatusEnum).find(
              (status) => status.value === row.queueable_status
            );
            return statusEnum ? statusEnum.label : "--";
          },
        },
        {
          label: "Date & Time",
          display(row) {
            let datetime = row.processed_at;

            if (
              row.queueable_status === ENUMS.QueueableStatusEnum.Errored.value
            )
              datetime = row.errored_at;
            if (
              row.queueable_status === ENUMS.QueueableStatusEnum.Completed.value
            )
              datetime = row.completed_at;

            return datetime ? dates.formatAsDateTime(datetime) : "--";
          },
        },
      ],
      contactsTableColumn: [],
      filterTableColumns: {},
      validationErrors: {},
      checkedActionRowsData: [],
      actionForm: {
        tags: [],
        types: [],
      },
      actions: [
        { id: 1, label: "Manage Types", modal: "typesModal" },
        { id: 2, label: "Manage Tags", modal: "tagsModal" },
      ],
    };
  },
  created() {
    this.$store.dispatch("contacts/setup", "contacts").then(() => {
      this.setupColumns();
    });
  },
  watch: {
    "$store.state.events.reloadContactTable"() {
      if (this.$store.state.events.reloadContactTable) {
        this.refreshTable();
        this.$store.commit("events/toggleEvent", "reloadContactTable");
      }
    },
  },
  computed: {
    hasExportIntegration() {
      if (!this.$store.state.accounts.features.Exports) return false;

      return Boolean(
        this.$store.getters.authUser.is_owner ||
          this.$store.getters.authUser.can_export_contacts
      );
    },
    contactTags() {
      return this.$store.getters.contactTags;
    },
    contactPresets() {
      return this.$store.getters.contactsTableConfigurations;
    },
    defaultPreset() {
      return this.contactPresets.find(
        (preset) =>
          preset.id ===
          this.$store.getters.authUser.default_contacts_table_preset_id
      );
    },
  },
  methods: {
    setupColumns() {
      // shallow copy columns state from leads store
      this.contactsTableColumn = Object.values(
        this.$store.getters["contacts/columns"]("contacts")
      );
      // deep copy columns state from contacts store. copying object without affecting original object
      this.filterTableColumns = utils.clone(
        this.$store.getters["contacts/columns"]("contacts")
      );
    },
    resetColumns() {
      for (let columnsKey in this.filterTableColumns) {
        this.filterTableColumns[columnsKey].visible = !(
          columnsKey === "phone2" || columnsKey === "street"
        );
      }
    },
    applyColumnsChange() {
      for (let columnsKey in this.filterTableColumns) {
        const filterColumn = this.filterTableColumns[columnsKey];
        const tableColumn = this.contactsTableColumn.find(
          (tableColumn) => tableColumn.field === filterColumn.field
        );

        this.$store.commit("contacts/setOneColumn", [
          "contacts",
          columnsKey,
          filterColumn.visible,
        ]);

        if (tableColumn) tableColumn.visible = filterColumn.visible;
      }
    },
    resetFilters() {
      this.$store.commit("contacts/resetFilters", "contacts");
    },
    downloadCSV(isSelected = false) {
      const filters = utils.clone(
        this.$store.getters["contacts/filters"]("contacts")
      );
      filters.download = true;
      filters.columns = this.contactsTableColumn
        .filter((column) => !!column.visible)
        .map((column) => {
          return {
            class: column.class,
            label: column.label,
            field: column.field,
          };
        });

      if (isSelected) {
        const contactIds = this.getContactIds();
        if (contactIds.length === 0) {
          Snackbar.open({
            message: "No contacts were selected",
            type: "is-danger",
            position: "is-bottom",
          });
          return;
        }

        filters.contact_ids = contactIds;
      }

      this.downloading = true;
      api.contacts
        .all(filters)
        .then((res) => {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(new Blob([res.data]));
          link.setAttribute("download", "contacts.csv");
          document.body.appendChild(link);
          link.click();
        })
        .finally(() => (this.downloading = false));
    },
    managePreset() {
      this.resetActivePresetTab();
      this.$refs.managePresetsModal.open();
    },
    changePreset(preset) {
      if (!preset) {
        this.resetColumns();
        this.resetFilters();
      } else {
        for (let columnKey in this.filterTableColumns) {
          const filterColumn = this.filterTableColumns[columnKey];
          const presetColumn = preset.columns.find(
            (column) => column.field === filterColumn.field
          );

          if (presetColumn) filterColumn.visible = presetColumn.visible;
        }

        for (const [filterKey, filterValue] of Object.entries(preset.filters)) {
          this.$store.commit("contacts/setOneFilter", [
            "contacts",
            filterKey,
            filterValue,
          ]);
        }
      }

      this.refreshTable();
    },
    handlePresetTabChange(tabIndex) {
      if (tabIndex === 0) {
        this.resetActivePresetTab();
        return;
      }

      this.activePresetId = this.contactPresets[tabIndex - 1].id;
    },
    resetActivePresetTab() {
      this.currentPresetTab = 0;
      this.activePresetId = 0;
    },
    manageRowsChecked(rows) {
      if (rows.length === 0) {
        this.resetActionForm();
        return;
      }

      this.showAction = true;
      this.checkedActionRowsData = rows;
    },
    runAction(action) {
      if (!action) return;
      this.$refs[action.modal].open();
    },
    resetActionForm() {
      this.$refs.contactsTableButtons.action = null;
      this.showAction = false;
      this.checkedActionRowsData = [];
      this.actionForm = {
        tags: [],
        types: [],
      };
    },
    getContactIds() {
      return this.checkedActionRowsData.map((row) => row.id);
    },
    saveTypes() {
      this.saving = true;
      api.contacts
        .bulkUpdateContactTypes({
          ids: this.getContactIds(),
          contact_type_ids: this.actionForm.types,
        })
        .then((res) => {
          this.$buefy.toast.open({
            message:
              "Manage Types action successfully ran. This might take time due to massive data being processed",
            type: "is-success",
            duration: 2500,
          });

          this.resetActionForm();
          this.$refs.typesModal.close();
        })
        .catch((error) => {
          if (error.response.status === 422)
            this.validationErrors = error.response.data.errors;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    saveTags() {
      this.saving = true;
      api.contacts
        .bulkUpdateTags({
          ids: this.getContactIds(),
          tag_ids: this.actionForm.tags.map((tag) => tag.id),
        })
        .then((res) => {
          this.$buefy.toast.open({
            message:
              "Manage Tags action successfully ran. This might take time due to massive data being processed",
            type: "is-success",
            duration: 2500,
          });

          this.resetActionForm();
          this.$refs.tagsModal.close();
        })
        .catch((error) => {
          if (error.response.status === 422)
            this.validationErrors = error.response.data.errors;
        })
        .finally(() => {
          this.saving = false;
        });
    },
    refreshTable() {
      return this.$refs.contactsTable.queryData();
    },
    loadContacts(query = "") {
      const filters = utils.clone(this.$store.getters["contacts/filters"]("contacts"));
      filters.page = 1;

      if (query.length !== 0) {
        const queryArray = query.split("&");
        filters.page = queryArray
          .find((str) => str.includes("page="))
          .split("=")[1];
        filters.per_page = queryArray
          .find((str) => str.includes("per_page="))
          .split("=")[1];
        filters.sort_field = queryArray
          .find((str) => str.includes("sort_field="))
          .split("=")[1];
        filters.sort_order = queryArray
          .find((str) => str.includes("sort_order="))
          .split("=")[1];
      }

      // prevents checkedRowData for duplicated contacts
      this.resetActionForm();

      this.filterCount = this.$store.getters["contacts/filterCount"];
      return api.contacts.all(filters).then((res) => {
        this.totalContacts = res.data.meta.total;
        this.applyColumnsChange();
        return res;
      });
    },
    refreshActionTable() {
      return this.$refs.actionsTable.queryData();
    },
    loadProcessedMassActions(query = "") {
      return api.contacts.getProcessedMassActions(query);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.contact-tooltip {
  margin: auto 0 auto 5px;
}
.modal-description {
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: normal;
}
.note {
  font-size: 0.875rem;
  color: $text-light;
  line-height: 1.2;
}
</style>
